import { gsap } from "gsap";
import {Flip} from 'gsap/Flip'
import {GSDevTools} from "gsap/GSDevTools"
import {MotionPathPlugin} from 'gsap/MotionPathPlugin'
import {MotionPathHelper} from 'gsap/MotionPathHelper'
import { debounced } from '../../_lib/utils'

export class CustomerJourney {
  constructor(container) {
    this.container = container
    // phase elements
    this.phaseSvgContainerStart = container.querySelector('.phase__svg-container.phase__svg-container--start')
    this.phaseSvgContainerEnd = container.querySelector('.phase__svg-container.phase__svg-container--end')
    this.svgPhase = container.querySelector('#svg_phase')
    this.phasePath = this.svgPhase.querySelector('#phase-path')
    this.phaseSegments = this.svgPhase.querySelectorAll('#phase-segments path')
    this.phaseNumbers = [...this.svgPhase.querySelectorAll('#numbers > g')]
    this.phaseNumbersGroup = this.svgPhase.querySelector('#numbers')
    this.phaseItems = container.querySelectorAll('.phase__wrapper .phase__item')

    //step elements
    this.stepSvgContainer = container.querySelector('.step__svg-container')
    this.stepsContainer = container.querySelector('.step__main')
    this.svgStep = this.container.querySelector('#svg_step')
    this.stepDots = this.svgStep.querySelectorAll("#dots circle")
    this.stepPath = this.svgStep.querySelector("#step #step-path")
    this.stepWrapper = container.querySelectorAll('.step__wrapper')
    this.avatar = container.querySelector('.avatar')
    this.stepBtns = container.querySelectorAll('.step__btn')

    this.activeStep = 0
    this.activeStepItemCount = 0
    this.activePhase = 0
    this.activeDot = -1
    this.reset = false

    //gsap
    this.tlStep = undefined,
    this.tlPhase = undefined,
    this.gsapConfig = {
      debug: true,
      //segment positions along the phasePath
      phaseSegments: [
        1,    //start 1
        0.805,    //start 2
        0.59,    //start 3
        0.4,    //start 4
        0.21,     //start 5
      ],
      //phase number positions in % along the phase timeline (multiplied by phaseRotation to get the timeline position in seconds)
      phaseStopPositions: [
        .0,
        .10,
        .23,
        .37,
        .54
      ],
      //dot positions in % along the step timeline (multiplied by stepDuration to get the timeline position in seconds)
      stepDots: [
        .07,
        .18,
        .3,
        .45,
        .64
      ],
      duration: {
        'step': 4,
        'phaseRotation': 1,
        'phaseInit': 1
      },
    }

    this.initGsap()
    //phase clicked once -> move to right col and show step
    this.addMainPhaseClickHandler()

    // click on dot -> move avatar, show text
    this.addStepDotsClickHandler()
    // click on step-btn -> move to phase
    this.addStepBtnsClickHandler()

    this.initResizeWatcher()
  }

  /**
   * reinit step timeline on window resize to update avatar path motion
   */
  initResizeWatcher() {
    window.addEventListener('resize', debounced(200, () => {
      this.tlStep.kill()
      this.tlStep = this.initTlStep()
    }))
  }


  /**
   * dots click moves avatar
   */
  addStepDotsClickHandler() {
    this.stepDots.forEach((dot, idx) => {
      dot.addEventListener('click', (e) => {
        // this.removeActive(this.stepDots)
        this.addActive(dot)
        this.moveAvatarToDot(dot.dataset.dot)
        this.activeStep = idx
        this.showStep(idx)
      })
    })
  }

  /**
   * step btn click -> move to phase
   */
  addStepBtnsClickHandler() {
    this.stepBtns.forEach((btn) => {
      const phase = parseInt(btn.dataset.phase)
      btn.addEventListener('click', (e) => {
        this.movePhasePosition(phase)
        this.showPhase(phase)
      })
    })
  }

  /**
   * rotate to Phase
   */
  addPhaseClickHandler() {
    this.phaseNumbers.forEach((number, idx) => {
      number.addEventListener('click', (e) => {
        this.movePhasePosition(idx)
        this.showPhase(idx)
      })
    })
  }

  /**
   * move main phase from left to right column, remove clickhandler after
   */
  addMainPhaseClickHandler() {
    this.phaseNumbers[0].addEventListener('click', (e) => {
      const state = Flip.getState(this.svgPhase)
      this.phaseSvgContainerEnd.appendChild(this.svgPhase)
      Flip.from(
        state,
        {
          // absolute: true,
          duration: this.gsapConfig.duration.phaseInit,
          onComplete: self => {
            this.addActive(this.stepSvgContainer)
            this.showPhase(0)
            this.addActive(this.phaseSegments[0])
            this.addActive(this.stepsContainer)
          },
        }
      )

      this.addPhaseClickHandler()
    }, {once: true})
  }

  showPhase(idx) {
    // console.log("showPhase", idx)
    this.removeActive(this.phaseItems)
    //remove previous step__item actives
    this.removeActive(this.stepWrapper[this.activePhase]?.childNodes)
    this.removeActive(this.stepSvgContainer)

    this.activePhase = idx
    this.activeStep = 0
    this.activeStepItemCount = this.stepWrapper[this.activePhase]?.childNodes.length
    this.reset = true

    this.showStep(0, true)
    //idx+1 (phase item 0 is overview)
    this.addActive(this.phaseItems[idx+1])
    this.removeActive(this.phaseNumbers)
    this.addActive(this.phaseNumbers[idx])

    //hide last dot if only 4 steps
    if(this.activeStepItemCount == 4) {
      [...this.stepDots].at(-1).classList.add('is-hidden')
    } else {
      [...this.stepDots].at(-1).classList.remove('is-hidden')
    }
  }

  /**
   *
   * @param idx
   */
  showStep(idx, reset = false) {
    let stepIdx = idx
    // console.log("showStep", idx, reset)

    if(reset) {
       stepIdx = -1
    //   this.reset = true
      this.removeActive(this.avatar)
      this.removeActive(this.stepDots)
    }

    this.moveAvatarToDot(stepIdx)
  }

  /**
   * move avatar to dot along step timeline
   * @param dot
   */
  moveAvatarToDot(dot) {
    let to = 0.01
    if(dot > -1) {
      to = this.gsapConfig.stepDots[dot] * this.gsapConfig.duration.step
      this.activeDot = dot
      this.addActive(this.stepDots[dot])
      //show step content
      let stepWrapper = this.stepWrapper[this.activePhase]
      this.removeActive(stepWrapper?.childNodes)
      this.addActive(stepWrapper?.childNodes[this.activeStep])
    }

    this.tlStep.tweenTo(to)
  }

  /**
   * rotate phase to clicked segment
   * @param idx
   */
  movePhasePosition(idx) {
    let to = this.gsapConfig.phaseStopPositions[idx] * this.gsapConfig.duration.phaseRotation
    this.tlPhase.tweenTo(to)
  }

  initGsap() {
    gsap.registerPlugin(
      MotionPathPlugin,
      Flip
    )

    // let tl = this.tlNumbers()
    this.tlStep = this.initTlStep()
    this.tlPhase = this.initTlPhases()

    if(this.gsapConfig.debug) {
      gsap.registerPlugin(GSDevTools, MotionPathHelper)
      // MotionPathHelper.create(this.phasePath)
    }

    /**
     * to update step dot positions enable devTools with this.tlStep and comment paused:true in tl
     */
    // GSDevTools.create(this.tlStep)
    // GSDevTools.create(this.tlPhase)
  }

  /**
   * step segment with avatar
   * @returns {*}
   */
  initTlStep() {
    const segments = this.gsapConfig.stepDots
    let tl = gsap.timeline({
      id: 'step',
      paused: true
    })

    tl.to(this.avatar,
      {
        duration: this.gsapConfig.duration.step,
        motionPath:{
          path: this.stepPath,
          // align: "self",
          align: this.stepPath,
          autoRotate: false,
          alignOrigin: [0.5, 0.5],
          // start: segments[0],
          // end: phaseSegments[1],
          // start: .6,
          // end: .4,
        }
      }
    )
    //event when avatar moves to starting position
    .call(() => {
        this.addActive(this.avatar)
        this.moveAvatarToDot(this.activeStep)
        this.addActive(this.stepSvgContainer)
      },
      null,
      0.01
    )

    return tl
  }


  initTlPhases() {
    const segments = this.gsapConfig.phaseSegments

    let tl = gsap.timeline({
      id: 'numbers',
      paused: true
    })
    this.phaseNumbers.forEach((number, idx) => {
      tl.to(number,
        {
          duration: this.gsapConfig.duration.phaseRotation,
          motionPath:{
            path: this.phasePath,
            // align: "self",
            align: this.phasePath,
            ease: "linear",
            autoRotate: false,
            alignOrigin: [0.5, 0.5],
            start: segments[idx],
            end: segments[idx] + 1,
          }
        },
        0
      )
    })

    return tl
  }

  /**
   * add is-active class to element
   * @param el
   */
  addActive(el) {
    el?.classList.add('is-active')
  }

  /**
   * remove is-active class from single element or NodeList
   * @param el
   */
  removeActive(el) {
    if (el instanceof HTMLElement) {
      el.classList.remove('is-active')
    } else {
      el?.forEach(item => {
        item.classList.remove('is-active')
      })
    }
  }
}

// initialize
const customerJourney = document.querySelector('.customer-journey');
if (customerJourney) {
  new CustomerJourney(customerJourney)
}
